/**
 * Fases do Cronograma CIPATR (NR-31)
 * Comissão Interna de Prevenção de Acidentes no Trabalho Rural
 * NR-31: Segurança e Saúde no Trabalho na Agricultura, Pecuária, 
 *        Silvicultura, Exploração Florestal e Aquicultura
 */

import { Fase } from '../types';
import { Users, FileText, Vote, CheckCircle, Award } from 'lucide-react';

export const criarFasesCipatr = (dataTerminoMandato: string): Fase[] => {
  const dataTermino = new Date(dataTerminoMandato);
  
  const calcularData = (diasAntes: number): Date => {
    const data = new Date(dataTermino);
    data.setDate(data.getDate() - diasAntes);
    return data;
  };

  return [
    // FASE 1: Formação da Comissão Eleitoral
    {
      id: 'fase1',
      titulo: 'Formação da Comissão Eleitoral',
      artigo: 'Art. 31.18.2 NR-31',
      prazoNR: '60 dias antes do término do mandato',
      data: calcularData(60),
      cor: 'bg-green-700',
      icon: Users,
      acoes: [
        'Designar membros da comissão eleitoral entre os membros atuais da CIPATR',
        'Definir presidente e secretário da comissão',
        'Elaborar cronograma detalhado do processo eleitoral',
        'Considerar especificidades do trabalho rural (sazonalidade, dispersão geográfica)'
      ]
    },
    
    // FASE 2: Comunicação ao Sindicato Rural
    {
      id: 'fase2',
      titulo: 'Comunicação ao Sindicato Rural',
      artigo: 'Art. 31.18.1.1 NR-31',
      prazoNR: '58 dias antes do término (após formação da comissão)',
      data: calcularData(58),
      cor: 'bg-amber-600',
      icon: FileText,
      acoes: [
        'Comunicar início do processo eleitoral ao sindicato da categoria de trabalhadores rurais',
        'Utilizar meio adequado considerando área rural (físico ou eletrônico)',
        'Manter comprovante da comunicação',
        'Aguardar eventual manifestação do sindicato rural'
      ]
    },
    
    // FASE 3: Publicação do Edital de Convocação
    {
      id: 'fase3',
      titulo: 'Publicação do Edital de Convocação',
      artigo: 'Art. 31.18.3(a) NR-31',
      prazoNR: '45 dias antes do término (abertura de prazos para inscrições)',
      data: calcularData(45),
      cor: 'bg-lime-600',
      icon: FileText,
      acoes: [
        'Publicar edital em locais de fácil acesso aos trabalhadores rurais',
        'Considerar dispersão geográfica das propriedades rurais',
        'Disponibilizar em locais de concentração (refeitórios, galpões, entrada da propriedade)',
        'Divulgar para trabalhadores permanentes e sazonais'
      ]
    },
    
    // FASE 4: Período de Inscrições
    {
      id: 'fase4',
      titulo: 'Período de Inscrições',
      artigo: 'Art. 31.18.3(b) NR-31',
      prazoNR: '44 a 29 dias antes (15 dias corridos - mínimo exigido)',
      data: calcularData(44),
      dataFim: calcularData(29),
      cor: 'bg-yellow-600',
      icon: Users,
      acoes: [
        'Receber inscrições de todos os trabalhadores rurais',
        'Fornecer comprovante de inscrição (físico ou eletrônico)',
        'Garantir acesso aos trabalhadores em diferentes áreas da propriedade',
        'Garantir estabilidade de emprego para todos os inscritos'
      ]
    },
    
    // FASE 5: Publicação da Lista de Candidatos
    {
      id: 'fase5',
      titulo: 'Publicação da Lista de Candidatos',
      artigo: 'Art. 31.18.3(e) NR-31',
      prazoNR: '28 dias antes (1 dia após término das inscrições)',
      data: calcularData(28),
      cor: 'bg-emerald-600',
      icon: FileText,
      acoes: [
        'Publicar relação completa dos trabalhadores rurais inscritos',
        'Divulgar em locais de fácil acesso e alta circulação',
        'Considerar trabalhadores em diferentes turnos e setores',
        'Verificar dados e informações dos candidatos'
      ]
    },
    
    // FASE 6: Eleição (Votação)
    {
      id: 'fase6',
      titulo: 'Eleição (Votação)',
      artigo: 'Art. 31.18.3(f) NR-31',
      prazoNR: '31 dias antes do término (mínimo exigido pela NR-31)',
      data: calcularData(31),
      cor: 'bg-orange-600',
      icon: Vote,
      acoes: [
        'Realizar eleição em dia normal de trabalho',
        'Organizar votação considerando dispersão geográfica dos trabalhadores',
        'Garantir voto secreto para todos os trabalhadores rurais',
        'Facilitar participação de trabalhadores em diferentes frentes de trabalho'
      ]
    },
    
    // FASE 7: Apuração dos Votos
    {
      id: 'fase7',
      titulo: 'Apuração dos Votos',
      artigo: 'Art. 31.18.3(i) NR-31',
      prazoNR: '30 dias antes (1 dia após eleição)',
      data: calcularData(30),
      cor: 'bg-teal-600',
      icon: CheckCircle,
      acoes: [
        'Apurar votos em horário normal de trabalho',
        'Permitir acompanhamento de representantes do empregador e trabalhadores',
        'Facultar acompanhamento dos candidatos',
        'Elaborar ata de eleição e apuração com ordem decrescente de votos'
      ]
    },
    
    // FASE 8: Treinamento Obrigatório CIPATR
    {
      id: 'fase8',
      titulo: 'Treinamento Obrigatório CIPATR',
      artigo: 'Art. 31.19.1 NR-31',
      prazoNR: '15 dias antes do término (antes da posse) - Mínimo 20 horas',
      data: calcularData(15),
      cor: 'bg-pink-600',
      icon: Award,
      acoes: [
        'Promover treinamento para membros titulares e suplentes (mínimo 20 horas)',
        'Incluir conteúdo sobre NR-31 e legislação rural',
        'Abordar riscos específicos: agrotóxicos, máquinas agrícolas, animais peçonhentos',
        'Incluir prevenção de acidentes com tratores, colheitadeiras e equipamentos rurais',
        'Capacitar sobre trabalho em altura (poda, colheita em árvores)',
        'Abordar riscos biológicos (zoonoses, leptospirose, tétano)'
      ]
    },
    
    // FASE 9: Posse dos Membros Eleitos
    {
      id: 'fase9',
      titulo: 'Posse dos Membros Eleitos',
      artigo: 'Art. 31.17.7 NR-31',
      prazoNR: '1 dia após término do mandato anterior',
      data: new Date(dataTermino.getTime() + 24 * 60 * 60 * 1000), // +1 dia
      cor: 'bg-red-600',
      icon: Award,
      acoes: [
        'Empossar membros eleitos e designados',
        'Fornecer cópias das atas de eleição e posse',
        'Designar presidente (representante do empregador rural)',
        'Eleição do vice-presidente (entre representantes dos empregados)',
        'Registrar posse considerando características do trabalho rural'
      ]
    }
  ];
};
